import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { InputFieldProps } from './InputField';

const defaultOnValidate = () => {
  return [];
};

/** Props to spread in the InputField component, from the UseNumericInputFieldReturnType hook */
export type NumericInputFieldProps = {
  onBlur: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
} & Pick<InputFieldProps, 'errors' | 'isActive' | 'onFocus' | 'value' | 'inputMode'>;

type UseNumericInputFieldReturnType = {
  /** Props to spread in the InputField component */
  props: NumericInputFieldProps;
  /** Manually set new value */
  setValue: (value: string) => void;
  /** Self-validate  */
  validateInputField: () => string[];
  /** Clear any errors */
  clearErrors: () => void;
  /** If value has changed since initialization. Used for keeping track of form changes */
  hasChangedSinceInit: () => boolean;
};

/** Will set the inputMode to numeric (numpad on mobiles) and clean up any non-numeric characters */
export const useNumericInputField = (
  /** Initial state value */
  initValue: string,
  /** Validate function called onBlur or when using the validateInputField function */
  validate: (value: string) => string[] = defaultOnValidate,
  /** Override clean up function */
  customFormat?: (value: string) => string,
  /** Set to true if validate should be called on blur even when value is empty */
  validateEmptyOnBlur = false
): UseNumericInputFieldReturnType => {
  const [value, setValue] = useState(
    customFormat && initValue ? customFormat(initValue) : initValue.replace(/\D/g, '')
  );
  const [isActive, setIsActive] = useState(!!initValue);
  const [used, setUsed] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const inputFieldProps: NumericInputFieldProps = useMemo(() => {
    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
      setUsed(true);
      const newValue = event.target.value;
      customFormat ? setValue(customFormat(newValue)) : setValue(newValue.replace(/\D/g, ''));
      if (errors.length > 0) {
        setErrors([]);
      }
    };

    const onFocus = () => {
      setIsActive(true);
    };

    const onBlur = () => {
      setIsActive(false);
      if (value || validateEmptyOnBlur || used) {
        const errorsArr = validate(value);
        setErrors(errorsArr);
      }
    };

    return {
      errors,
      isActive,
      onBlur,
      onChange,
      onFocus,
      value,
      inputMode: 'numeric',
    };
  }, [errors, isActive, value, customFormat, validateEmptyOnBlur, used, validate]);

  useEffect(() => {
    if (value || (validateEmptyOnBlur && value !== initValue)) {
      const errorsArr = validate(value);
      setErrors(errorsArr);
    }
    // Validate again if validation function changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate]);

  const manuallySetValue = useCallback(
    (newValue: string) => {
      customFormat ? setValue(customFormat(newValue)) : setValue(newValue.replace(/\D/g, ''));
    },
    [customFormat]
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const validateInputField = useCallback(() => {
    const errorsArr = validate(value);
    setErrors(errorsArr);
    return errorsArr;
  }, [validate, value]);

  const hasChangedSinceInit = useCallback(() => {
    return value.replace(/\D/g, '') !== initValue.replace(/\D/g, '');
  }, [initValue, value]);

  return {
    props: inputFieldProps,
    setValue: manuallySetValue,
    validateInputField,
    clearErrors,
    // rename to has changed?
    hasChangedSinceInit,
  };
};

type UseNumericInputFieldAsyncReturnType = Omit<UseNumericInputFieldReturnType, 'validateInputField'> & {
  validateInputField: () => Promise<string[]>;
};

/** Support an async validator */
export const useNumericInputFieldAsync = (
  /** Initial state value */
  initValue: string,
  /** Validate function called onBlur or when using the validateInputField function
   * important - memoize this function, to not trigger unwanted reevaluations
   */
  validate: (value: string) => string[] = defaultOnValidate,
  /* Asynchronous validation to run after valid synchronous validation. */
  asyncValidate: (value: string) => Promise<string[]>,
  /** Override clean up function */
  customFormat?: (value: string) => string,
  /** Set to true if validate should be called on blur even when value is empty */
  validateEmptyOnBlur = false
): UseNumericInputFieldAsyncReturnType => {
  const [value, setValue] = useState(
    customFormat && initValue ? customFormat(initValue) : initValue.replace(/\D/g, '')
  );
  const [isActive, setIsActive] = useState(!!initValue);
  const [errors, setErrors] = useState<string[]>([]);

  const inputFieldProps: NumericInputFieldProps = useMemo(() => {
    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      customFormat ? setValue(customFormat(newValue)) : setValue(newValue.replace(/\D/g, ''));
      if (errors.length > 0) {
        setErrors([]);
      }
    };

    const onFocus = () => {
      setIsActive(true);
    };

    const onBlur = async () => {
      setIsActive(false);
      if (value || validateEmptyOnBlur) {
        const errorsArr = validate(value);
        setErrors(errorsArr);
        if (errorsArr.length === 0) {
          const errorsArr = await asyncValidate(value);
          setErrors(errorsArr);
        }
      }
    };

    return {
      errors,
      isActive,
      onBlur,
      onChange,
      onFocus,
      value,
      inputMode: 'numeric',
    };
  }, [errors, isActive, value, customFormat, validateEmptyOnBlur, validate, asyncValidate]);

  useEffect(() => {
    if (value || (validateEmptyOnBlur && value !== initValue)) {
      const errorsArr = validate(value);
      setErrors(errorsArr);
      if (errorsArr.length === 0) {
        asyncValidate(value).then(errors => setErrors(errors));
      }
    }
    // Validate again if validation function changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate]);

  const manuallySetValue = useCallback(
    (newValue: string) => {
      customFormat ? setValue(customFormat(newValue)) : setValue(newValue.replace(/\D/g, ''));
    },
    [customFormat]
  );

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const validateInputField = useCallback(async () => {
    let errorsArr = validate(value);
    setErrors(errorsArr);
    if (asyncValidate && errorsArr.length === 0) {
      errorsArr = await asyncValidate(value);
      setErrors(errorsArr);
    }
    return errorsArr;
  }, [asyncValidate, validate, value]);

  const hasChangedSinceInit = useCallback(() => {
    return value.replace(/\D/g, '') !== initValue.replace(/\D/g, '');
  }, [initValue, value]);

  return {
    props: inputFieldProps,
    setValue: manuallySetValue,
    validateInputField,
    clearErrors,
    hasChangedSinceInit,
  };
};
